import './BetbookPosts.less';

import React, { useEffect, useLayoutEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { navigate } from '@/routing';
import i18n from 'nike-ui/src/common/i18n';
import BetbookPostItem from './BetbookPostItem';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import FloatingButton from 'nike-ui/src/components/floating-button/FloatingButton';
import InfiniteScroll from '@/responsive/components/InfiniteScroll/InfiniteScroll';
import LoaderMini from '@/ui/views/LoaderMini';
import { loadAuthorBetbookPosts, loadMoreAuthorBetbookPosts, setScroll } from '@/betbook/handlers';
import {
    hasNextPage$,
    initialBetbookPostsLoadingDone$,
    loadingBetbookPostsPending$,
    posts$,
    scrollPosition$
} from '@/betbook/subs';
import { accountVerificationStatus$, loggedIn$ } from '@/account/subs';

import BetbookFilters from '@/betbook/views/BetbookFilters';
import { NotVerifiedInfo } from '@/betbook/views/BetbookPosts';
import NotFoundPostsIcon from 'nike-ui/src/icons/NotFoundPostsIcon';
import { betbookNewRoute } from '@/betbook/utils';
import { BetboookPostsSortTypeOpions, BetbooPostTypeOptions } from 'nike-ui/src/components/betbook/constants';
import { useBetbookLastParam } from 'nike-ui/src/components/betbook/utils';
import MultipleComponentsLoader from '@/common/components/loaders/MultipleComponentsLoader';
import BetbookPostItemLoader from '@/betbook/views/BetbookPostItemLoader';

interface BetbookPostsAuthorProps {
    postType: BetbooPostTypeOptions;
    sortType?: BetboookPostsSortTypeOpions;
    nickname: string;
}
function BetbookPostsAuthor({ nickname, postType, sortType }: BetbookPostsAuthorProps) {
    const { subscribe } = useReframe();
    const [lastParam, setLastParam] = useBetbookLastParam();
    const scrollPosition = subscribe(scrollPosition$) || 0;
    const posts = subscribe(posts$);
    const initialLoadingDone = subscribe(initialBetbookPostsLoadingDone$);
    const loadingPending = subscribe(loadingBetbookPostsPending$);
    const hasNextPage = subscribe(hasNextPage$);
    const loggedIn = subscribe(loggedIn$);
    const verificationStatus = subscribe(accountVerificationStatus$);
    const isVerified = verificationStatus === 'VERIFIED';
    const betbookViewPort = document.querySelector('.layout-main-col');
    const floatingBtnDisabled = !loggedIn || (loggedIn && !isVerified);

    useLayoutEffect(() => {
        if (betbookViewPort && window.matchMedia('(min-width: 800px)')) {
            betbookViewPort.scrollTo(0, scrollPosition);
        }
    }, [scrollPosition, betbookViewPort]);
    useEffect(() => {
        if (
            !lastParam ||
            lastParam?.nickname !== nickname ||
            lastParam?.postType !== postType ||
            lastParam?.sortType !== sortType
        ) {
            dispatch(loadAuthorBetbookPosts(nickname, postType, 0, sortType));
            setLastParam({ nickname, postType, sortType });
        }
    }, [lastParam, nickname, postType, setLastParam, sortType]);

    function handlePositionMemoize() {
        betbookViewPort && dispatch(setScroll(betbookViewPort?.scrollTop));
    }

    function handleNewPost() {
        if (loggedIn) {
            navigate(betbookNewRoute(postType));
        } else {
            dispatch(['login.openScreen']);
        }
    }

    function handleLoadMore() {
        dispatch(loadMoreAuthorBetbookPosts(postType));
    }

    return (
        <div onClick={handlePositionMemoize}>
            {loggedIn && !isVerified && <NotVerifiedInfo verificationStatus={verificationStatus} />}
            <BetbookFilters postType={postType} />
            {initialLoadingDone ? (
                posts?.length ? (
                    <InfiniteScroll
                        className="betbook-posts"
                        hasMore={hasNextPage && !loadingPending}
                        loadMore={handleLoadMore}
                        loader={<LoaderMini className="infinite-loader betbook-more-posts-loader" key="loader" />}
                        threshold={0}
                        initialLoad={false}
                    >
                        {posts.map(post => (
                            <BetbookPostItem key={post.id} postType={postType} post={post} />
                        ))}
                        <FloatingButton
                            onClick={handleNewPost}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </InfiniteScroll>
                ) : (
                    <div className="betbook-posts">
                        <EmptyState
                            text={i18n('betbook.author.posts.empty')}
                            textAboveIcon
                            stateIcon={<NotFoundPostsIcon />}
                        />
                        <FloatingButton
                            onClick={handleNewPost}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </div>
                )
            ) : (
                <div className="betbook-posts">
                    <MultipleComponentsLoader itemsCount={2} componentLoader={BetbookPostItemLoader} />
                </div>
            )}
        </div>
    );
}

export default BetbookPostsAuthor;
